import { generateId } from "./generate-id";
import { envConstants } from "../../constant/env-constant";

type LogLevel = "info" | "warn" | "error" | "debug";

const LEVEL_RANK: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
};

/**
 * Level gate. Checked BEFORE formatMessage so suppressed levels pay neither
 * the JSON.stringify nor the console/stdout cost. Default level is info;
 * hot-path per-request logs use debug and are skipped unless
 * UF_LOG_LEVEL=debug is set (see env-constant.ts LOG_LEVEL).
 * The level is resolved once lazily — UF_LOG_LEVEL is static per deployment,
 * and reading process.env on every (suppressed) log call is avoidable cost.
 */
let resolvedLevelRank: number | null = null;
function shouldLog(level: LogLevel): boolean {
  if (resolvedLevelRank === null) {
    const configured = envConstants.LOG_LEVEL as LogLevel;
    resolvedLevelRank = LEVEL_RANK[configured] ?? LEVEL_RANK.info;
  }
  return LEVEL_RANK[level] <= resolvedLevelRank;
}

// Redact secret-shaped values from ALL structured logs. `envConstants` (and the
// raw AWS secrets objects) are serialized wholesale at boot via logServiceEvent;
// without this, once CACHE_PASSWORD / the various *_TOKEN getters are provisioned
// they are written to stdout in cleartext (confirmed finding — see
// REDIS-CUTOVER-PREFLIGHT §2.9). JSON.stringify invokes every enumerable getter,
// so redaction must run in the replacer. Redacting a benign key that happens to
// match is a safe over-correction; leaking a credential is not.
// Key-name patterns for values that must never reach logs. `auth(?!or)` matches
// AUTH as a component (`SERVICE_ABILITY_AUTH`, `API_AUTH_KEY`, `AUTH_TOKEN`) but
// NOT `author`/`authorId`; `authorization` is listed explicitly since the
// lookahead excludes it. Bare `session` is intentionally omitted so the
// `pageSessionId`/`sessionId` request-correlation ids survive — real session
// secrets are `*Token`/`*Cookie` (caught by `token`, or by the object-blanking
// of the raw secrets dump). This must catch every real secret without redacting
// correlation ids; the logger.spec pins that contract.
const SECRET_KEY_RE =
  /(password|token|secret|credential|authorization|auth(?!or)|(api|app|access)[_-]?key)/i;
// Credentials embedded in a URI's userinfo (redis://:pw@host, https://u:tok@host)
// leak through error messages/stacks regardless of the property name. The
// password class is greedy and the lookahead anchors on the LAST `@` before a
// host so an (unencoded) `@` inside the password can't leak its tail.
const URI_USERINFO_RE = /(\/\/[^/\s:@]*):[^/\s]+@(?=[^@/\s]*(?:[:/\s]|$))/g;
function redactSecrets(key: string, value: unknown): unknown {
  if (key && SECRET_KEY_RE.test(key) && value != null) {
    // Redact EVERY value type under a secret-shaped key — string, object/array,
    // number, boolean. A type-specific guard is a recurring leak source: it let
    // objects (JSON.stringify then recursed into nested keys) and numeric/boolean
    // values through. Empty strings are kept (already safe, preserves the "unset"
    // signal). Non-string secret values are blanked wholesale.
    if (typeof value === "string") {
      return value.length > 0 ? "[REDACTED]" : value;
    }
    return "[REDACTED]";
  }
  // Value-level (any key): strip credentials embedded in a URI, e.g. a caught
  // error whose message/stack echoes a connection string.
  if (typeof value === "string" && value.includes("@")) {
    return value.replace(URI_USERINFO_RE, "$1:[REDACTED]@");
  }
  return value;
}

function formatMessage(
  level: LogLevel,
  module: string,
  message: string,
  meta?: Record<string, any>
) {
  const buildNumber = envConstants.BUILD_VERSION;
  const prefix = `[${level.toUpperCase()}] [${module}-${buildNumber}]`;
  const metaStr = meta ? ` ${JSON.stringify(meta, redactSecrets)}` : "";
  return `${prefix} ${message}${metaStr}`;
}
export function getLogger(module: string) {
  return {
    info: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("info")) {
        console.info(formatMessage("info", module, message, meta));
      }
    },
    warn: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("warn")) {
        console.warn(formatMessage("warn", module, message, meta));
      }
    },
    error: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("error")) {
        console.error(formatMessage("error", module, message, meta));
      }
    },
    debug: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("debug")) {
        console.debug(formatMessage("debug", module, message, meta));
      }
    }
  };
}

/* -------------------------------------------------------

   Page Request Logger → Storyblok pages

------------------------------------------------------- */

export function getPageSessionId() {
  return generateId();
}

export function logPageRequest(
  path: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`PageTracker:${path}`);

  logger.info(message, {
    path,
    ...(meta || {})
  });
}

/**
 * Debug-level twin of logPageRequest for per-request verbose logs.
 * Same logger name and shape — set UF_LOG_LEVEL=debug to re-enable.
 */
export function logPageRequestDebug(
  path: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`PageTracker:${path}`);

  logger.debug(message, {
    path,
    ...(meta || {})
  });
}

/* -------------------------------------------------------

   Service Logger → for business/infra

------------------------------------------------------- */

export function logServiceEvent(
  serviceName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedServiceName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${serviceName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Service:${updatedServiceName}`);
  logger.info(message, meta);
}

/* -------------------------------------------------------

   Orchestration Logger → for workflows

------------------------------------------------------- */

export function logOrchestrationEvent(
  orchestrationName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedOrchestrationName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${orchestrationName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Orchestration:${updatedOrchestrationName}`);
  logger.info(message, meta);
}

/* -------------------------------------------------------

   Error Logger → generic + page errors

------------------------------------------------------- */

export function logError(
  scope: string, // e.g. "StoryblokService" or "CacheWarmup"
  error: unknown,
  meta?: Record<string, any>
) {
  const updateScope = `${meta?.prefix ? `${meta?.prefix}-` : ""}${scope}${
    meta?.suffix ? `-${meta?.suffix}` : ""
  }`;
  const logger = getLogger(`Error:${updateScope}`);
  if (error instanceof Error) {
    logger.error("Error occurred", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      ...(meta || {})
    });
  } else {
    logger.error("Error occurred", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logPageError(
  scope: string, // e.g. "getResolvedPageComponents" or "PageRenderer"
  error: unknown,
  meta?: Record<string, any>
) {
  const logger = getLogger("PageTracker");
  if (error instanceof Error) {
    logger.error("Page error", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      scope,
      ...(meta || {})
    });
  } else {
    logger.error("Page error", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logPageInfo(
  scope: string, // e.g. "getResolvedPageComponents" or "PageRenderer"
  error: unknown,
  meta?: Record<string, any>
) {
  const logger = getLogger("PageTracker");
  if (error instanceof Error) {
    logger.info("Page info", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      scope,
      ...(meta || {})
    });
  } else {
    logger.info("Page info", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logCornJobEvent(
  jobName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`CronJob:${jobName}`);
  logger.info(message, meta);
}

export function logAdapterEvent(
  adapterName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Adapter:${adapterName}`);
  logger.info(message, meta);
}

export function logGenericEvent(
  loggerName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(loggerName);
  logger.info(message, meta);
}

/**
 * Debug-level twin of logGenericEvent for per-request verbose logs.
 * Same logger name and shape — set UF_LOG_LEVEL=debug to re-enable.
 */
export function logGenericDebug(
  loggerName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(loggerName);
  logger.debug(message, meta);
}

/**
 * Debug-level twin of logServiceEvent for per-request verbose logs.
 */
export function logServiceDebug(
  serviceName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedServiceName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${serviceName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Service:${updatedServiceName}`);
  logger.debug(message, meta);
}

/**
 * Debug-level twin of logAdapterEvent for per-request verbose logs.
 */
export function logAdapterDebug(
  adapterName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Adapter:${adapterName}`);
  logger.debug(message, meta);
}

export function logRouteEvent(
  routeName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Route:${routeName}`);
  logger.info(message, meta);
}
